import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Text, Select, Button, ButtonGroup } from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine
} from 'recharts';

interface PriceData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  date: string;
}

interface PriceChartProps {
  symbol: string;
  data?: PriceData[];
  height?: number;
}

const PriceChart: React.FC<PriceChartProps> = ({ 
  symbol, 
  data = [], 
  height = 400 
}) => {
  const [timeframe, setTimeframe] = useState('1D');
  const [chartType, setChartType] = useState('line');
  const [priceData, setPriceData] = useState<PriceData[]>([]);
  const [loading, setLoading] = useState(false);

  // Generate mock price data if none provided
  useEffect(() => {
    if (data.length > 0) {
      setPriceData(data);
    } else {
      generateMockData();
    }
  }, [data, timeframe]);

  const generateMockData = () => {
    setLoading(true);
    const now = Date.now();
    const intervals = {
      '1H': 60,
      '1D': 24 * 60,
      '1W': 7 * 24 * 60,
      '1M': 30 * 24 * 60,
      '3M': 90 * 24 * 60,
      '1Y': 365 * 24 * 60
    };

    const interval = intervals[timeframe as keyof typeof intervals] || 60;
    const points = timeframe === '1H' ? 60 : timeframe === '1D' ? 24 : 30;
    
    const mockData: PriceData[] = [];
    let basePrice = Math.random() * 1000 + 100;
    
    for (let i = points - 1; i >= 0; i--) {
      const timestamp = now - (i * interval * 60 * 1000);
      const volatility = 0.02; // 2% volatility
      const change = (Math.random() - 0.5) * volatility;
      
      basePrice = basePrice * (1 + change);
      const high = basePrice * (1 + Math.random() * 0.02);
      const low = basePrice * (1 - Math.random() * 0.02);
      const open = i === points - 1 ? basePrice : mockData[mockData.length - 1].close;
      
      mockData.push({
        timestamp,
        open,
        high,
        low,
        close: basePrice,
        volume: Math.random() * 1000000,
        date: new Date(timestamp).toLocaleTimeString()
      });
    }
    
    setPriceData(mockData);
    setLoading(false);
  };

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(value);
  };

  const formatVolume = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toFixed(0);
  };

  const currentPrice = priceData[priceData.length - 1]?.close || 0;
  const previousPrice = priceData[priceData.length - 2]?.close || 0;
  const priceChange = currentPrice - previousPrice;
  const priceChangePercent = (priceChange / previousPrice) * 100;

  return (
    <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Text fontSize="xl" fontWeight="bold" color="gray.800">
            {symbol} Price Chart
          </Text>
          <Flex alignItems="center" gap={2}>
            <Text fontSize="2xl" fontWeight="bold">
              {formatPrice(currentPrice)}
            </Text>
            <Text
              fontSize="sm"
              color={priceChange >= 0 ? 'green.500' : 'red.500'}
              fontWeight="medium"
            >
              {priceChange >= 0 ? '+' : ''}{formatPrice(priceChange)} ({priceChangePercent.toFixed(2)}%)
            </Text>
          </Flex>
        </Box>

        <Flex gap={3} alignItems="center">
          <ButtonGroup size="sm" isAttached variant="outline">
            {['1H', '1D', '1W', '1M', '3M', '1Y'].map((tf) => (
              <Button
                key={tf}
                onClick={() => setTimeframe(tf)}
                colorScheme={timeframe === tf ? 'blue' : 'gray'}
                variant={timeframe === tf ? 'solid' : 'outline'}
              >
                {tf}
              </Button>
            ))}
          </ButtonGroup>

          <Select value={chartType} onChange={(e) => setChartType(e.target.value)} size="sm" w="120px">
            <option value="line">Line</option>
            <option value="area">Area</option>
            <option value="candlestick">Candlestick</option>
          </Select>
        </Flex>
      </Flex>

      <Box height={`${height}px`}>
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'area' ? (
            <AreaChart data={priceData}>
              <defs>
                <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3182CE" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3182CE" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="date" 
                stroke="#718096"
                fontSize={12}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return timeframe === '1H' ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : timeframe === '1D' ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : date.toLocaleDateString();
                }}
              />
              <YAxis 
                stroke="#718096"
                fontSize={12}
                tickFormatter={(value) => formatPrice(value)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                formatter={(value: number) => [formatPrice(value), 'Price']}
                labelFormatter={(label) => `Time: ${label}`}
              />
              <Area
                type="monotone"
                dataKey="close"
                stroke="#3182CE"
                strokeWidth={2}
                fill="url(#priceGradient)"
              />
            </AreaChart>
          ) : chartType === 'candlestick' ? (
            <LineChart data={priceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="date" 
                stroke="#718096"
                fontSize={12}
              />
              <YAxis 
                stroke="#718096"
                fontSize={12}
                tickFormatter={(value) => formatPrice(value)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                formatter={(value: number, name: string) => [formatPrice(value), name]}
              />
              <Line type="monotone" dataKey="high" stroke="#22C55E" strokeWidth={1} name="High" dot={false} />
              <Line type="monotone" dataKey="low" stroke="#EF4444" strokeWidth={1} name="Low" dot={false} />
              <Line type="monotone" dataKey="open" stroke="#F59E0B" strokeWidth={2} name="Open" dot={false} />
              <Line type="monotone" dataKey="close" stroke="#3B82F6" strokeWidth={2} name="Close" />
            </LineChart>
          ) : (
            <LineChart data={priceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="date" 
                stroke="#718096"
                fontSize={12}
              />
              <YAxis 
                stroke="#718096"
                fontSize={12}
                tickFormatter={(value) => formatPrice(value)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                formatter={(value: number) => [formatPrice(value), 'Price']}
                labelFormatter={(label) => `Time: ${label}`}
              />
              <Line
                type="monotone"
                dataKey="close"
                stroke="#3182CE"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#3182CE' }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </Box>

      {/* Volume Chart */}
      <Box height="100px" mt={4}>
        <Text fontSize="sm" color="gray.600" mb={2}>Volume</Text>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={priceData}>
            <XAxis dataKey="date" hide />
            <YAxis hide />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                fontSize: '12px'
              }}
              formatter={(value: number) => [formatVolume(value), 'Volume']}
            />
            <Area
              type="monotone"
              dataKey="volume"
              stroke="#8B5CF6"
              fill="#8B5CF6"
              fillOpacity={0.3}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PriceChart;